/**
 * InfinitSales - Card de evento na thread
 *
 * Aviso de sistema no meio da conversa, no estilo do WhatsApp: não é balão,
 * não tem autor e não compete com as mensagens. Hoje cobre os eventos de
 * transferência (`contact_assignment_events`), que existiam só no drawer —
 * quem lê a conversa não via em que ponto o atendimento mudou de mão nem por
 * quê.
 *
 * O card entra na ordenação por timestamp junto com as mensagens, então
 * atravessa os separadores de data naturalmente.
 *
 * Usado pelos 4 inboxes; toda cor vem dos tokens do tema (claro e escuro).
 */
import React, { useState } from 'react';
import { ArrowRight, ArrowRightLeft, Bot, GitBranch, UserCheck, UserMinus } from 'lucide-react';

// Acima disto o motivo é cortado e ganha o "ver mais". Corte por caractere (e
// não por altura) para o card não depender de medição de layout.
const REASON_LIMIT = 130;

/** Ícone semântico do evento: quem passou a atender define o desenho. */
const eventIcon = (event) => {
  const type = event?.event_type;
  if (type === 'cleared' || type === 'paused') return UserMinus;
  if (type === 'label') return GitBranch;
  const nextType = event?.next?.type;
  if (nextType === 'agent') return Bot;
  if (nextType === 'member') return UserCheck;
  return ArrowRightLeft;
};

const formatEventTime = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const ThreadEventCard = ({ event, colors }) => {
  const [expanded, setExpanded] = useState(false);
  if (!event) return null;

  const Icon = eventIcon(event);
  const prevName = event.prev?.name || '';
  const nextName = event.next?.name || '';
  const isRemoval = event.event_type === 'cleared' || event.event_type === 'paused';

  // Ator só aparece quando acrescenta informação: no handoff o ator É o agente
  // que estava atendendo (prev), e repetir o nome só polui.
  const actorName = event.actor?.name;
  const showActor = !!actorName && event.actor?.id !== event.prev?.id;

  const reason = (event.reason || '').trim();
  const reasonLong = reason.length > REASON_LIMIT;
  const reasonShown = (!reasonLong || expanded)
    ? reason
    : `${reason.slice(0, REASON_LIMIT).trimEnd()}…`;

  const styles = {
    wrapper: {
      display: 'flex',
      justifyContent: 'center',
      margin: '12px 0',
    },
    card: {
      maxWidth: '70%',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      padding: '7px 12px',
      borderRadius: '8px',
      backgroundColor: colors.bgTertiary,
      color: colors.textSecondary,
      boxShadow: colors.shadowSm,
    },
    headline: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '6px',
      fontSize: '12px',
      lineHeight: 1.3,
    },
    name: {
      color: colors.textPrimary,
      fontWeight: 500,
      overflowWrap: 'anywhere',
    },
    meta: {
      color: colors.textTertiary,
      whiteSpace: 'nowrap',
    },
    reason: {
      fontSize: '12px',
      lineHeight: 1.4,
      color: colors.textSecondary,
      overflowWrap: 'anywhere',
      margin: 0,
    },
    toggle: {
      alignSelf: 'flex-start',
      background: 'transparent',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      fontSize: '11px',
      fontWeight: 600,
      color: colors.accentPrimary,
    },
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headline}>
          <Icon size={14} style={{ flexShrink: 0, color: colors.textTertiary }} />
          {isRemoval ? (
            <span>
              {event.event_type === 'paused' ? 'Sem responsável' : 'Responsável removido'}
              {prevName ? <> · era <span style={styles.name}>{prevName}</span></> : null}
            </span>
          ) : (
            <>
              {prevName ? (
                <>
                  <span style={styles.name}>{prevName}</span>
                  <ArrowRight size={12} style={{ flexShrink: 0, color: colors.textTertiary }} />
                </>
              ) : (
                <span>Atribuído a</span>
              )}
              <span style={styles.name}>{nextName || 'Sem responsável'}</span>
            </>
          )}
          {showActor && <span style={styles.meta}>· por {actorName}</span>}
          <span style={styles.meta}>{formatEventTime(event.timestamp)}</span>
        </div>

        {reason && (
          <p style={styles.reason}>
            {reasonShown}
            {reasonLong && (
              <>
                {' '}
                <button
                  type="button"
                  style={styles.toggle}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded ? 'ver menos' : 'ver mais'}
                </button>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default ThreadEventCard;
